import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { useAccountApplicationsQuery } from './useAccountApplicationsQuery';
import { useAccountApplicationsSearch } from './useAccountApplicationsSearch';
import { AccountApplication } from '../types';

// DEFAULT: newest first, keyed on `created`.
//
// ⚠️ `created` is NULLABLE on AccountApplication (AWSDateTime, not
// AWSDateTime!), which is not the immutable-and-non-null key the checklist
// prefers — a nullable key degrades to insertion order if EVERY row lacks it.
// There is no better option on this type: `id`, `accountId` and
// `applicationId` are opaque ids, and sorting an operator's list by one of
// them is meaningless.
//
// `created` is still the right key. It is immutable, unlike `updated`, which
// would reorder the list under someone reading it, and it agrees with the
// order listAccountApplications already returns, so the first paint does not
// reshuffle the server's response. Rows missing it sort last, which is what a
// missing timestamp should mean.
const DEFAULT_SORT_STATUS: DataTableSortStatus<AccountApplication> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const useAccountApplications = (accountId: string) => {

    if (typeof accountId === 'undefined') {
        throw new Error('accountId is required parameter');
    }

    const query = useAccountApplicationsQuery({ accountId });
    const { keyword } = useAccountApplicationsSearch();

    // Type guard, not a bare flatMap: a page can come back undefined while a
    // fetch is in flight, and spreading that would throw rather than render an
    // empty table.
    const items: AccountApplication[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages
    // FETCHED SO FAR. Under token pagination the browser cannot filter or
    // reorder rows it has never seen, so the footer states a LOADED count
    // rather than a total, and `unfilteredCount` travels with the match count
    // so an operator can see the search was scoped to a window.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter((item: AccountApplication) =>
        !needle ||
        item.applicationId?.toLowerCase().includes(needle) ||
        item.application?.name?.toLowerCase().includes(needle)
    );

    // Sort state is component-level, not the module-scoped store: sort is how
    // ONE operator is viewing the table right now, unlike the keyword, which is
    // shared with the sibling search input.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<AccountApplication>(matched, DEFAULT_SORT_STATUS);

    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        visibleCount: visibleItems.length,
        unfilteredCount: items.length,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
    };
};
